import { useMutation, useQuery } from "@apollo/client";
import {
  CheckCircleIcon,
  ChevronDownIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  EyeSlashIcon,
  FilmIcon,
  GiftIcon,
  NoSymbolIcon,
  PauseIcon,
  PlayIcon,
  SparklesIcon,
  TrashIcon,
  XCircleIcon,
  XMarkIcon,
} from "@heroicons/react/24/solid";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  useTheme,
} from "@mui/material";
import { addDays, format } from "date-fns";
import { useEffect, useRef, useState } from "react";
import Hls from "hls.js";
import { useParams } from "react-router-dom";
import styled from "styled-components";
import { ErrorAlert } from "../../components/ErrorAlert/ErrorAlert";
import Loader from "../../components/Loader/Loader";
import ChangeUserGender from "../../components/ChangeUserGender/ChangeUserGender";
import ResetProfilePhotoApproval from '../../components/ResetProfilePhotoApproval/ResetProfilePhotoApproval';
import RejectUserPhoto from '../../components/RejectUserPhoto/RejectUserPhoto';
import Page from "../../components/Page/Page";
import Panel from "../../components/Panel/Panel";
import PlatformIcon from "../../components/PlatformIcon/PlatformIcon";
import Badge from "../../components/Styled/Badge";
import {
  MediaBadges,
  MediaCard,
  MediaFrame,
  MediaFooter,
  MediaGrid,
  MediaImage,
  MediaTop,
} from "../../components/Styled/Media";
import Text from "../../components/Styled/Text";
import { useDismissableSnackbar } from "../../hooks/useDismissableSnackbar";
import {
  DiamondHistoryEntry,
  DiamondType,
  Photo,
  ProfileTranslation,
  Reel,
  ReelRejectReason,
  ReelsApprovalResult,
  ReelsApprovalSort,
  User,
  UserDiamondStatus,
} from "../../graphql/graphql";
import {
  APPROVE_REEL,
  DELETE_USER,
  SET_BAN,
  SET_LIVE_BAN,
  SET_LIVE_STREAMING_APPROVED,
} from "../../graphql/mutations-gql";
import {
  REELS_APPROVAL,
  TRANSLATE_PROFILE,
  USER,
  USER_DIAMOND_STATUS,
} from "../../graphql/queries-gql";
import {
  CloseButton,
  HlsPill,
  ModalContent,
  ModalOverlay,
  PlayPauseButton,
  ReelVideo,
  TimeDisplay,
  VideoBadge,
  VideoContainer,
  VideoControls,
  VideoControlsRow,
  VideoProgress,
  VideoTimeline,
} from "../reels/styled";

const UserId = styled.span`
  font-family: ${(props) => props.theme.monoFontFamily};
  font-size: 12.5px;
  color: ${(props) => props.theme.secondaryTextColor};
  user-select: all;
`;

const Summary = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  gap: 20px 24px;
`;

const ProfilePhotoColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
`;

const ProfilePhotoFrame = styled.div<{ $clickable: boolean }>`
  width: 144px;
  aspect-ratio: 3 / 4;
  border-radius: 10px;
  overflow: hidden;
  border: 1px solid ${(props) => props.theme.border};
  background-color: ${(props) => props.theme.surfaceRaised};
  ${(props) => (props.$clickable ? "cursor: pointer;" : "")}
`;

const ProfilePhoto = styled.img`
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const NoProfilePhoto = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${(props) => props.theme.mutedTextColor};
  font-size: 13px;
`;

const SummaryMain = styled.div`
  flex: 1;
  min-width: 260px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const BadgeRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
`;

const BadgeDivider = styled.span`
  width: 1px;
  height: 16px;
  margin: 0 4px;
  background-color: ${(props) => props.theme.border};
`;

const InfoList = styled.dl`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(170px, 1fr));
  gap: 14px 20px;
  margin: 0;
`;

const InfoItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
`;

const InfoLabel = styled.dt`
  font-size: 12px;
  font-weight: 500;
  color: ${(props) => props.theme.secondaryTextColor};
`;

const InfoValue = styled.dd`
  margin: 0;
  font-size: 13.5px;
  font-weight: 500;
  color: ${(props) => props.theme.textColor};
  font-variant-numeric: tabular-nums;
  overflow-wrap: anywhere;
`;

const ProfileValue = styled(InfoValue)`
  display: flex;
  flex-direction: column;
  gap: 2px;
  font-weight: 400;
`;

const SectionGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(360px, 1fr));
  gap: 20px;
  align-items: start;

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const FieldList = styled.dl`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin: 0;
`;

const ProfileText = styled.span`
  font-size: 14px;
  color: ${(props) => props.theme.textColor};
  white-space: pre-wrap;
  word-break: break-word;
`;

const ProfileTranslationText = styled.span`
  display: flex;
  align-items: baseline;
  gap: 6px;
  font-size: 13px;
  color: ${(props) => props.theme.secondaryTextColor};
  white-space: pre-wrap;
  word-break: break-word;
`;

const PanelActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const MutedText = styled.span`
  font-size: 13px;
  color: ${(props) => props.theme.secondaryTextColor};
`;

const DiamondStatsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
  margin-bottom: 16px;
`;

const DiamondStatCell = styled.div<{ $accent?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 12px 16px;
  border-radius: 10px;
  border: 1px solid
    ${(props) =>
      props.$accent ? props.theme.colors.blue : props.theme.border};
  background-color: ${(props) =>
    props.$accent ? props.theme.colors.blueSoft : "transparent"};
  min-width: 0;
`;

const DiamondStatLabel = styled.span`
  font-size: 12px;
  font-weight: 500;
  color: ${(props) => props.theme.secondaryTextColor};
`;

const DiamondStatValue = styled.span`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 20px;
  font-weight: 650;
  letter-spacing: -0.01em;
  font-variant-numeric: tabular-nums;
  color: ${(props) => props.theme.textColor};
`;

const DiamondHistoryList = styled.div`
  display: flex;
  flex-direction: column;
  max-height: 420px;
  overflow-y: auto;
  border: 1px solid ${(props) => props.theme.border};
  border-radius: 10px;
`;

const DiamondHistoryRow = styled.div`
  display: grid;
  grid-template-columns: 36px 1fr auto auto;
  align-items: center;
  gap: 12px;
  padding: 8px 12px;
  border-bottom: 1px solid ${(props) => props.theme.border};

  &:last-child {
    border-bottom: none;
  }
`;

const DiamondTypeIcon = styled.div<{ kind: "gift" | "video" }>`
  width: 36px;
  height: 36px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${(props) =>
    props.kind === "gift"
      ? props.theme.colors.redSoft
      : props.theme.colors.blueSoft};
  color: ${(props) =>
    props.kind === "gift" ? props.theme.colors.red : props.theme.colors.blue};
`;

const HistoryReelThumb = styled.img`
  width: 36px;
  height: 36px;
  border-radius: 8px;
  object-fit: cover;
`;

const HistoryPrimary = styled.span`
  font-size: 13.5px;
  font-weight: 500;
  color: ${(props) => props.theme.textColor};
`;

const HistorySecondary = styled.span`
  font-size: 12px;
  color: ${(props) => props.theme.secondaryTextColor};
  font-variant-numeric: tabular-nums;
`;

const HistoryAmount = styled.span`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 14px;
  font-weight: 650;
  font-variant-numeric: tabular-nums;
  color: ${(props) => props.theme.colors.blue};
`;

const HistoryDate = styled.span`
  font-size: 12px;
  color: ${(props) => props.theme.secondaryTextColor};
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
`;

const EmptyHistory = styled.div`
  padding: 24px;
  text-align: center;
  font-size: 13px;
  color: ${(props) => props.theme.secondaryTextColor};
  border: 1px dashed ${(props) => props.theme.borderStrong};
  border-radius: 10px;
`;

const HistoryTextCol = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

const PhotoImage = styled(MediaImage)<{ $dimmed: boolean }>`
  ${(props) => (props.$dimmed ? "opacity: 0.4;" : "")}
`;

const CollapsibleHeader = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 14px 18px;
  background: none;
  border: none;
  border-radius: 12px;
  cursor: pointer;
  color: ${(props) => props.theme.textColor};
  font: inherit;
  font-size: 14px;
  font-weight: 600;
  text-align: left;
  transition: background-color 0.15s ease;

  &:hover {
    background-color: ${(props) => props.theme.surfaceHover};
  }
`;

const Chevron = styled.div<{ open: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${(props) => props.theme.secondaryTextColor};
  transition: transform 150ms ease;
  transform: rotate(${(props) => (props.open ? 180 : 0)}deg);
`;

const RawDataBlock = styled.pre`
  margin: 0 18px 18px;
  padding: 14px 16px;
  background-color: ${(props) => props.theme.inputBg};
  border: 1px solid ${(props) => props.theme.border};
  border-radius: 10px;
  font-size: 12px;
  line-height: 1.5;
  color: ${(props) => props.theme.textColor};
  font-family: ${(props) => props.theme.monoFontFamily};
  overflow: auto;
  max-height: 520px;
`;

const getProfilePhotoStatus = (
  profilePhoto: Photo | null | undefined,
  profilePhotoApproved: boolean | null | undefined
): 'approved' | 'pending' | 'rejected' | 'none' => {
  if (!profilePhoto || !profilePhoto.uploadComplete) return 'none';
  if (profilePhotoApproved === true) return 'approved';
  if (profilePhotoApproved === false) return 'rejected';
  return 'pending';
};

const formatDate = (date: string | null | undefined): string => {
  if (!date) return '-';
  return format(new Date(date), 'MMM d, yyyy HH:mm');
};

const formatHistoryDate = (date: string | Date | null | undefined): string => {
  if (!date) return '-';
  return format(new Date(date), 'MMM d, yyyy');
};

const UserPage = () => {
  const { id } = useParams();
  const { data, loading, error } = useQuery(USER, {
    fetchPolicy: "network-only",
    variables: {
      _id: id,
    },
  });
  const {
    data: reelsData,
    loading: reelsLoading,
  } = useQuery<{ reelsApproval: ReelsApprovalResult }>(REELS_APPROVAL, {
    variables: {
      args: {
        sort: ReelsApprovalSort.Newest,
        search: id,
      },
    },
    skip: !id,
  });
  const {
    data: diamondData,
    loading: diamondLoading,
    error: diamondError,
  } = useQuery<{ userDiamondStatus: UserDiamondStatus }>(USER_DIAMOND_STATUS, {
    variables: { userId: id },
    skip: !id,
    fetchPolicy: "network-only",
  });
  const loadedUser: User | undefined = data?.user;
  const hasProfileText = Boolean(
    loadedUser?.firstName?.trim() ||
      loadedUser?.lastName?.trim() ||
      loadedUser?.describeYourself?.trim(),
  );
  const {
    data: translationData,
    loading: translationLoading,
    error: translationError,
  } = useQuery<{ translateProfile: ProfileTranslation }>(TRANSLATE_PROFILE, {
    variables: { userId: id },
    skip: !id || !hasProfileText,
  });
  const theme = useTheme();
  const [setBan] = useMutation(SET_BAN);
  const [deleteUser] = useMutation(DELETE_USER);
  const [approveReel] = useMutation(APPROVE_REEL);
  const [setLiveBan] = useMutation(SET_LIVE_BAN);
  const [setLiveStreamingApproved] = useMutation(SET_LIVE_STREAMING_APPROVED);
  const { showSnackbar } = useDismissableSnackbar();

  // Live streaming access (trusted creators gate). Reject and revoke go through
  // a small dialog so every "no" carries a reason the user will see.
  const [liveDecisionOpen, setLiveDecisionOpen] = useState(false);
  const [liveDecisionReason, setLiveDecisionReason] = useState("");
  const [liveDecisionDays, setLiveDecisionDays] = useState("14");

  const approveLiveStreaming = () => {
    setLiveStreamingApproved({
      variables: { userId: id, value: true },
      onCompleted: () => showSnackbar("Live streaming approved", "success"),
      onError: (e) => showSnackbar("Failed: " + e.message, "error"),
      refetchQueries: [USER],
    });
  };

  const submitLiveDecision = () => {
    const days = liveDecisionDays.trim() === "" ? null : Number(liveDecisionDays);
    setLiveStreamingApproved({
      variables: {
        userId: id,
        value: false,
        reason: liveDecisionReason.trim() || null,
        retryAfterDays: days !== null && Number.isFinite(days) ? Math.max(0, Math.floor(days)) : null,
      },
      onCompleted: () => {
        setLiveDecisionOpen(false);
        setLiveDecisionReason("");
        setLiveDecisionDays("14");
        showSnackbar("Live streaming access removed", "success");
      },
      onError: (e) => showSnackbar("Failed: " + e.message, "error"),
      refetchQueries: [USER],
    });
  };

  const [selectedReel, setSelectedReel] = useState<Reel | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [rawOpen, setRawOpen] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const hlsRef = useRef<Hls | null>(null);

  const userReels: Reel[] = reelsData?.reelsApproval.searchedReels || [];

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !selectedReel?.url) return;

    const url = selectedReel.url;
    const isHls = url.endsWith(".m3u8");

    if (hlsRef.current) {
      hlsRef.current.destroy();
      hlsRef.current = null;
    }

    const tryPlay = () => {
      video.play().catch(() => {
        video.muted = true;
        video.play().catch(() => {});
      });
    };

    if (isHls && Hls.isSupported()) {
      const hls = new Hls();
      hls.loadSource(url);
      hls.attachMedia(video);
      hls.on(Hls.Events.MANIFEST_PARSED, tryPlay);
      hlsRef.current = hls;
    } else {
      video.src = url;
      video.addEventListener("loadedmetadata", tryPlay, { once: true });
    }

    return () => {
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
    };
  }, [selectedReel]);

  const handleReelClick = (reel: Reel) => {
    setSelectedReel(reel);
    setIsPlaying(false);
    if (videoRef.current) {
      videoRef.current.currentTime = 0;
    }
  };

  const handleCloseModal = () => {
    setSelectedReel(null);
  };

  const handlePlayPause = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const handleRejectReel = (reelId: string) => {
    approveReel({
      variables: {
        reelId,
        value: false,
        rejectReason: ReelRejectReason.Inappropriate,
        explicit: false,
        matchVideo: false,
      },
      onCompleted: () => {
        showSnackbar("Reel rejected", "success");
        handleCloseModal();
      },
      onError: (error) => {
        showSnackbar("Failed to reject reel. " + error.message, "error");
      },
      refetchQueries: [REELS_APPROVAL],
    });
  };

  const formatTime = (time: number) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, "0")}`;
  };

  const showImage = (src: string) => {
    window.open(src, "_blank");
  };

  if (loading) {
    return (
      <Page title="User">
        <Loader />
      </Page>
    );
  }
  if (error) {
    return (
      <Page title="User">
        <ErrorAlert error={error} />
      </Page>
    );
  }
  const user: User = data.user;
  const profilePhoto: Photo | null = data.user.profilePhoto;
  const photos: Photo[] = data.user.photos || [];
  const profilePhotoStatus = getProfilePhotoStatus(profilePhoto, user.profilePhotoApproved);
  const hasProfilePhoto = Boolean(profilePhoto && profilePhoto.uploadComplete);
  const liveApplication = user.liveStreamingApplication;

  const handleBan = (value: boolean, days?: number) => {
    let until: Date | null = null;
    if (days) {
      until = addDays(new Date(), days);
    }
    setBan({
      variables: {
        _id: user._id,
        value,
        until,
      },
      onCompleted: (data) => {
        console.log("setBan onCompleted", data);
      },
      onError: (error) => {
        console.log("setBan onError", error);
      },
      refetchQueries: [USER],
    });
  };

  const handleDelete = () => {
    deleteUser({
      variables: {
        userId: user._id,
      },
      onCompleted: (data) => {
        console.log("deleteUser onCompleted", data);
      },
      onError: (error) => {
        console.log("deleteUser onError", error);
      },
      refetchQueries: [USER],
    });
  };

  const handleLiveBan = (value: boolean) => {
    setLiveBan({
      variables: { userId: user._id, value },
      onCompleted: () =>
        showSnackbar(
          value ? 'User banned from live streaming' : 'Live ban removed',
          'success'
        ),
      onError: (e) => showSnackbar('Failed: ' + e.message, 'error'),
      refetchQueries: [USER],
    });
  };

  const liveApprovedTitle = `by ${user.liveStreamingApprovedBy ?? "?"} ${
    user.liveStreamingApprovedDate
      ? format(new Date(user.liveStreamingApprovedDate), "yyyy-MM-dd")
      : ""
  }`;

  const liveApplicationTitle = liveApplication
    ? [
        `applied ${format(new Date(liveApplication.date), "yyyy-MM-dd")}`,
        liveApplication.decidedBy
          ? `decided by ${liveApplication.decidedBy}`
          : "",
        liveApplication.reason ? `reason: ${liveApplication.reason}` : "",
        liveApplication.retryAfterDate
          ? `may re-apply ${format(new Date(liveApplication.retryAfterDate), "yyyy-MM-dd")}`
          : "",
      ]
        .filter(Boolean)
        .join(" | ")
    : "";

  const liveApplicationTone =
    liveApplication?.status === "pending"
      ? "orange"
      : liveApplication?.status === "approved"
        ? "green"
        : "neutral";

  return (
    <Page
      title={user.username || "User"}
      description={<UserId>{user._id}</UserId>}
      actions={
        <>
          {user.banned ? (
            <Button
              size="small"
              variant="contained"
              color="success"
              onClick={() => handleBan(false)}
            >
              Remove ban
            </Button>
          ) : (
            <>
              <Button
                size="small"
                variant="outlined"
                color="warning"
                startIcon={<NoSymbolIcon width={15} />}
                onClick={() => handleBan(true, 10)}
              >
                Ban 10 days
              </Button>
              <Button
                size="small"
                variant="outlined"
                color="warning"
                startIcon={<NoSymbolIcon width={15} />}
                onClick={() => handleBan(true, 365)}
              >
                Ban 1 year
              </Button>
            </>
          )}
          <Button
            size="small"
            variant="outlined"
            color="error"
            startIcon={<TrashIcon width={15} />}
            onClick={() => handleDelete()}
          >
            Delete user
          </Button>
        </>
      }
    >
      {/* Summary: profile photo, status and key facts */}
      <Panel padding={20}>
        <Summary>
          <ProfilePhotoColumn>
            <ProfilePhotoFrame
              $clickable={hasProfilePhoto}
              onClick={
                hasProfilePhoto && profilePhoto
                  ? () => showImage(profilePhoto.url)
                  : undefined
              }
              title={hasProfilePhoto ? "Open full size" : undefined}
            >
              {hasProfilePhoto && profilePhoto ? (
                <ProfilePhoto src={profilePhoto.url} alt="Profile" />
              ) : (
                <NoProfilePhoto>No photo</NoProfilePhoto>
              )}
            </ProfilePhotoFrame>
            {profilePhotoStatus === 'approved' && (
              <Badge $tone="green">
                <CheckCircleIcon />
                Approved
              </Badge>
            )}
            {profilePhotoStatus === 'pending' && (
              <Badge $tone="orange">
                <ClockIcon />
                Pending review
              </Badge>
            )}
            {profilePhotoStatus === 'rejected' && (
              <Badge $tone="red">
                <XCircleIcon />
                Not approved
              </Badge>
            )}
            {hasProfilePhoto && profilePhoto && !user.deleted && (
              <RejectUserPhoto
                key={`${user._id}/${profilePhoto.uploadId}`}
                userId={user._id}
                uploadId={profilePhoto.uploadId}
              />
            )}
            {hasProfilePhoto && !user.deleted && (
              <ResetProfilePhotoApproval
                key={user._id}
                userId={user._id}
                approved={user.profilePhotoApproved}
              />
            )}
          </ProfilePhotoColumn>

          <SummaryMain>
            <BadgeRow>
              {user.gender && <Badge>{user.gender}</Badge>}
              {!user.deleted && (
                <ChangeUserGender key={user._id} userId={user._id} gender={user.gender} />
              )}
              {user.platform && (
                <Badge>
                  <PlatformIcon platform={user.platform} color="currentColor" />
                  {user.platform}
                </Badge>
              )}
              {user.country && <Badge>{user.country}</Badge>}
              {(user.banned ||
                user.deleted ||
                user.premium ||
                user.admin ||
                user.liveBan) && <BadgeDivider />}
              {user.banned && (
                <Badge $tone="red">
                  {user.bannedUntilDate
                    ? `Banned until ${formatDate(user.bannedUntilDate)}`
                    : 'Banned'}
                </Badge>
              )}
              {user.deleted && <Badge $tone="red">Deleted</Badge>}
              {user.premium && <Badge $tone="purple">Premium</Badge>}
              {user.admin && <Badge $tone="blue">Admin</Badge>}
              {user.liveBan && <Badge $tone="red">Live banned</Badge>}
              {user.liveStreamingApproved && (
                <Badge $tone="green" title={liveApprovedTitle}>
                  Live approved
                </Badge>
              )}
              {liveApplication && (
                <Badge $tone={liveApplicationTone} title={liveApplicationTitle}>
                  {`Live application MG-${liveApplication.ticketNumber}: ${liveApplication.status}`}
                </Badge>
              )}
            </BadgeRow>

            <InfoList>
              <InfoItem>
                <InfoLabel>Email</InfoLabel>
                <InfoValue>{user.email || '-'}</InfoValue>
              </InfoItem>
              <InfoItem>
                <InfoLabel>Created</InfoLabel>
                <InfoValue>{formatDate(user.created)}</InfoValue>
              </InfoItem>
              <InfoItem>
                <InfoLabel>Last active</InfoLabel>
                <InfoValue>{formatDate(user.lastActive)}</InfoValue>
              </InfoItem>
              <InfoItem>
                <InfoLabel>Last login</InfoLabel>
                <InfoValue>{formatDate(user.loginDate)}</InfoValue>
              </InfoItem>
              <InfoItem>
                <InfoLabel>App version</InfoLabel>
                <InfoValue>{user.appVersion}</InfoValue>
              </InfoItem>
              <InfoItem>
                <InfoLabel>Credits</InfoLabel>
                <InfoValue>{(user.credits ?? 0).toLocaleString()}</InfoValue>
              </InfoItem>
              <InfoItem>
                <InfoLabel>Translation tokens</InfoLabel>
                <InfoValue>
                  {(user.translationTokens ?? 0).toLocaleString()}
                </InfoValue>
              </InfoItem>
            </InfoList>
          </SummaryMain>
        </Summary>
      </Panel>

      <SectionGrid>
        {/* Profile Section */}
        <Panel title="Profile">
          <FieldList>
            {(
              [
                ['First name', 'firstName'],
                ['Last name', 'lastName'],
                ['Describe yourself', 'describeYourself'],
              ] as const
            ).map(([label, field]) => {
              const original = user[field]?.trim();
              const translated = translationData?.translateProfile[field]?.trim();
              return (
                <InfoItem key={field}>
                  <InfoLabel>{label}</InfoLabel>
                  <ProfileValue>
                    <ProfileText>{original || '-'}</ProfileText>
                    {original && translationLoading && (
                      <ProfileTranslationText>Translating...</ProfileTranslationText>
                    )}
                    {translated && translated !== original && (
                      <ProfileTranslationText>
                        <Badge $tone="blue">EN</Badge>
                        <span>{translated}</span>
                      </ProfileTranslationText>
                    )}
                  </ProfileValue>
                </InfoItem>
              );
            })}
            {translationError && <ErrorAlert error={translationError} />}
          </FieldList>
        </Panel>

        {/* Live streaming access and live ban */}
        <Panel
          title="Live streaming"
          actions={
            <PanelActions>
              {user.liveBan ? (
                <Button
                  size="small"
                  variant="contained"
                  color="success"
                  onClick={() => handleLiveBan(false)}
                >
                  Remove live ban
                </Button>
              ) : (
                <Button
                  size="small"
                  variant="outlined"
                  color="warning"
                  onClick={() => handleLiveBan(true)}
                >
                  Ban from live
                </Button>
              )}
              {user.liveStreamingApproved ? (
                <Button
                  size="small"
                  variant="outlined"
                  color="error"
                  onClick={() => setLiveDecisionOpen(true)}
                >
                  Revoke live access
                </Button>
              ) : (
                <>
                  {liveApplication?.status === "pending" && (
                    <Button
                      size="small"
                      variant="outlined"
                      color="error"
                      onClick={() => setLiveDecisionOpen(true)}
                    >
                      Reject live application
                    </Button>
                  )}
                  <Button
                    size="small"
                    variant="contained"
                    color="success"
                    onClick={() => approveLiveStreaming()}
                  >
                    {liveApplication?.status === "pending"
                      ? "Approve live application"
                      : "Approve for live"}
                  </Button>
                </>
              )}
            </PanelActions>
          }
        >
          <FieldList>
            <InfoItem>
              <InfoLabel>Access</InfoLabel>
              <InfoValue>
                {user.liveStreamingApproved ? (
                  <Badge $tone="green">Approved</Badge>
                ) : (
                  <Badge>Not approved</Badge>
                )}
                {user.liveStreamingApproved && (
                  <MutedText> {liveApprovedTitle}</MutedText>
                )}
              </InfoValue>
            </InfoItem>
            <InfoItem>
              <InfoLabel>Live ban</InfoLabel>
              <InfoValue>
                {user.liveBan ? (
                  <Badge $tone="red">Banned from live</Badge>
                ) : (
                  <Badge>Not banned</Badge>
                )}
              </InfoValue>
            </InfoItem>
            <InfoItem>
              <InfoLabel>Application</InfoLabel>
              <InfoValue>
                {liveApplication ? (
                  <>
                    <Badge $tone={liveApplicationTone}>
                      {`MG-${liveApplication.ticketNumber}: ${liveApplication.status}`}
                    </Badge>
                    <MutedText> {liveApplicationTitle}</MutedText>
                  </>
                ) : (
                  <MutedText>No application</MutedText>
                )}
              </InfoValue>
            </InfoItem>
          </FieldList>
        </Panel>
      </SectionGrid>

      <Dialog open={liveDecisionOpen} onClose={() => setLiveDecisionOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>
          {user.liveStreamingApproved ? "Revoke live streaming access" : "Reject live streaming application"}
        </DialogTitle>
        <DialogContent>
          <TextField
            label="Reason shown to the user"
            value={liveDecisionReason}
            onChange={(e) => setLiveDecisionReason(e.target.value)}
            fullWidth
            multiline
            minRows={2}
            margin="normal"
            inputProps={{ maxLength: 300 }}
          />
          <TextField
            label="May apply again after (days), empty = never"
            value={liveDecisionDays}
            onChange={(e) => setLiveDecisionDays(e.target.value.replace(/[^0-9]/g, ""))}
            fullWidth
            margin="normal"
          />
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" color="inherit" onClick={() => setLiveDecisionOpen(false)}>
            Cancel
          </Button>
          <Button variant="contained" color="error" onClick={() => submitLiveDecision()}>
            {user.liveStreamingApproved ? "Revoke" : "Reject"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Diamonds Section */}
      <Panel title="Diamonds">
        {diamondLoading ? (
          <Loader />
        ) : diamondError ? (
          <ErrorAlert error={diamondError} />
        ) : diamondData ? (
          <>
            <DiamondStatsRow>
              <DiamondStatCell $accent>
                <DiamondStatLabel>Current balance</DiamondStatLabel>
                <DiamondStatValue>
                  <SparklesIcon width={18} color={theme.colors.blue} />
                  {diamondData.userDiamondStatus.currentDiamonds.toLocaleString()}
                </DiamondStatValue>
              </DiamondStatCell>
              <DiamondStatCell>
                <DiamondStatLabel>Total earned</DiamondStatLabel>
                <DiamondStatValue>
                  {diamondData.userDiamondStatus.totalEarned.toLocaleString()}
                </DiamondStatValue>
              </DiamondStatCell>
              <DiamondStatCell>
                <DiamondStatLabel>Total paid out</DiamondStatLabel>
                <DiamondStatValue>
                  {diamondData.userDiamondStatus.totalPaid.toLocaleString()}
                </DiamondStatValue>
              </DiamondStatCell>
            </DiamondStatsRow>
            {diamondData.userDiamondStatus.currentDiamondHistory.length > 0 ? (
              <DiamondHistoryList>
                {diamondData.userDiamondStatus.currentDiamondHistory.map(
                  (entry: DiamondHistoryEntry, idx: number) => {
                    const isGift = entry.type === DiamondType.Gift;
                    return (
                      <DiamondHistoryRow key={`${entry.date}-${idx}`}>
                        {isGift ? (
                          <DiamondTypeIcon kind="gift">
                            <GiftIcon width={18} />
                          </DiamondTypeIcon>
                        ) : entry.reelThumbnailUrl ? (
                          <HistoryReelThumb src={entry.reelThumbnailUrl} alt="" />
                        ) : (
                          <DiamondTypeIcon kind="video">
                            <FilmIcon width={18} />
                          </DiamondTypeIcon>
                        )}
                        <HistoryTextCol>
                          <HistoryPrimary>
                            {isGift
                              ? entry.giftType
                                ? `Gift · ${entry.giftType}`
                                : 'Gift'
                              : 'Video views'}
                          </HistoryPrimary>
                          {!isGift && entry.qualifiedViews != null && (
                            <HistorySecondary>
                              {entry.qualifiedViews.toLocaleString()} qualified views
                            </HistorySecondary>
                          )}
                        </HistoryTextCol>
                        <HistoryAmount>
                          +{entry.diamondsEarned.toLocaleString()}
                          <SparklesIcon width={14} />
                        </HistoryAmount>
                        <HistoryDate>{formatHistoryDate(entry.date)}</HistoryDate>
                      </DiamondHistoryRow>
                    );
                  }
                )}
              </DiamondHistoryList>
            ) : (
              <EmptyHistory>No unpaid diamond earnings yet.</EmptyHistory>
            )}
          </>
        ) : null}
      </Panel>

      {/* Photo Gallery Section */}
      {photos.length > 0 && (
        <Panel title={`Photo gallery (${photos.length})`}>
          <MediaGrid $min={130} $gap={12}>
            {photos.map((photo) => {
              const isRejected = photo.verified === true && photo.approved === false;
              const isPending = !photo.verified;
              return (
                <MediaCard key={photo.uploadId}>
                  <MediaFrame
                    $ratio="3 / 4"
                    onClick={() => showImage(photo.url)}
                    title="Open full size"
                  >
                    <PhotoImage
                      src={photo.thumbnailUrl}
                      alt=""
                      $dimmed={isRejected}
                    />
                    <MediaTop>
                      <MediaBadges>
                        {isRejected && (
                          <Badge $solid $tone="red">
                            <ExclamationTriangleIcon />
                            Rejected
                          </Badge>
                        )}
                        {isPending && (
                          <Badge $solid $tone="orange">
                            <ClockIcon />
                            Pending
                          </Badge>
                        )}
                        {photo.approved && (
                          <Badge $solid $tone="green">
                            <CheckCircleIcon />
                            Approved
                          </Badge>
                        )}
                      </MediaBadges>
                      {photo.isPrivate && (
                        <MediaBadges $align="end">
                          <Badge $solid $tone="blue">
                            <EyeSlashIcon />
                            Private
                          </Badge>
                        </MediaBadges>
                      )}
                    </MediaTop>
                  </MediaFrame>
                  {!user.deleted && (
                    <MediaFooter>
                      <RejectUserPhoto
                        userId={user._id}
                        uploadId={photo.uploadId}
                      />
                    </MediaFooter>
                  )}
                </MediaCard>
              );
            })}
          </MediaGrid>
        </Panel>
      )}

      {/* Reels Section */}
      {(userReels.length > 0 || reelsLoading) && (
        <Panel title={`Reels${!reelsLoading ? ` (${userReels.length})` : ""}`}>
          {reelsLoading ? (
            <Loader />
          ) : (
            <MediaGrid $min={160} $gap={12}>
              {userReels.map((reel) => (
                <MediaCard key={reel._id}>
                  <MediaFrame onClick={() => handleReelClick(reel)}>
                    <MediaImage src={reel.thumbnailUrl ?? ""} alt="" />
                    <MediaTop>
                      <MediaBadges>
                        {reel.hlsReady && (
                          <Badge $solid $tone="purple">HLS</Badge>
                        )}
                        {reel.explicit && (
                          <Badge $solid $tone="red">18+</Badge>
                        )}
                      </MediaBadges>
                      <MediaBadges $align="end">
                        <Badge $solid>
                          {reel.matchVideo ? "Match" : "General"}
                        </Badge>
                      </MediaBadges>
                    </MediaTop>
                  </MediaFrame>
                </MediaCard>
              ))}
            </MediaGrid>
          )}
        </Panel>
      )}

      {/* Reel Video Modal */}
      {selectedReel && (
        <ModalOverlay onClick={handleCloseModal}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <CloseButton onClick={handleCloseModal} title="Close">
              <XMarkIcon width={24} />
            </CloseButton>

            <VideoContainer>
              <ReelVideo
                ref={videoRef}
                onEnded={() => {
                  setIsPlaying(false);
                  if (videoRef.current) videoRef.current.currentTime = 0;
                }}
                onClick={handlePlayPause}
                onTimeUpdate={() => {
                  if (videoRef.current)
                    setCurrentTime(videoRef.current.currentTime);
                }}
                onLoadedMetadata={() => {
                  if (videoRef.current)
                    setDuration(videoRef.current.duration);
                }}
                playsInline
              />
              <VideoControls>
                <VideoTimeline
                  onClick={(e) => {
                    if (videoRef.current) {
                      const rect = e.currentTarget.getBoundingClientRect();
                      const pos = (e.clientX - rect.left) / rect.width;
                      videoRef.current.currentTime = pos * duration;
                    }
                  }}
                >
                  <VideoProgress
                    progress={(currentTime / duration) * 100}
                  />
                </VideoTimeline>
                <VideoControlsRow>
                  <PlayPauseButton
                    onClick={handlePlayPause}
                    title={isPlaying ? "Pause" : "Play"}
                  >
                    {isPlaying ? <PauseIcon /> : <PlayIcon />}
                  </PlayPauseButton>
                  <TimeDisplay>
                    {formatTime(currentTime)} / {formatTime(duration)}
                  </TimeDisplay>
                  <IconButton
                    onClick={() => handleRejectReel(selectedReel._id)}
                    sx={{
                      color: theme.colors.red,
                      padding: "4px",
                      // Sits on top of the video, so a fixed light hover
                      "&:hover": {
                        backgroundColor: "rgba(255, 255, 255, 0.1)",
                      },
                    }}
                    title="Reject as inappropriate"
                  >
                    <NoSymbolIcon width={20} />
                  </IconButton>
                </VideoControlsRow>
              </VideoControls>
              <VideoBadge>
                {selectedReel.hlsReady && <HlsPill>HLS</HlsPill>}
                <Text size={14} weight={600} color="white">
                  {selectedReel.matchVideo ? "Match video" : "General video"}
                </Text>
              </VideoBadge>
            </VideoContainer>
          </ModalContent>
        </ModalOverlay>
      )}

      {/* Raw User Data (collapsed by default) */}
      <Panel padding={0}>
        <CollapsibleHeader
          type="button"
          onClick={() => setRawOpen((v) => !v)}
          aria-expanded={rawOpen}
        >
          Raw user data
          <Chevron open={rawOpen}>
            <ChevronDownIcon width={18} />
          </Chevron>
        </CollapsibleHeader>
        {rawOpen && (
          <RawDataBlock>{JSON.stringify(user, null, 2)}</RawDataBlock>
        )}
      </Panel>
    </Page>
  );
};

export default UserPage;
